"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { usePathname } from "@/i18n/routing";
import { parseActiveFromPathname } from "@/lib/navigation-config";
import type { TournamentSectionId } from "@/types/navigation";

export type NavigationApi = {
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;

  isMenuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  toggleMenu: () => void;

  expandedL1: Set<string>;
  isExpanded: (l1Id: string) => boolean;
  toggleExpand: (l1Id: string) => void;

  activeL1: TournamentSectionId | null;
  activeL2: string | null;
};

/**
 * State machine for the new mobile navigation: drawer open/closed, the
 * collapsible level-2 menu, and which L1 sections are expanded. Active
 * location is derived from `usePathname()` so it stays in lock-step with
 * the URL — the path also serves as the auto-close trigger.
 */
export function useNavigation(): NavigationApi {
  // next-intl's usePathname returns the canonical internal form
  // (`/tournaments/[tournamentSlug]/matches`) regardless of the user's
  // locale, so the parser only needs to know the English route shape.
  const pathname = usePathname();
  const { activeL1, activeL2 } = useMemo(
    () => parseActiveFromPathname(pathname),
    [pathname],
  );

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [expandedL1, setExpandedL1] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!activeL1) return;
    setExpandedL1((prev) => {
      if (prev.has(activeL1)) return prev;
      const next = new Set(prev);
      next.add(activeL1);
      return next;
    });
  }, [activeL1]);

  useEffect(() => {
    setMenuOpen(false);
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isDrawerOpen) setDrawerOpen(false);
      else if (isMenuOpen) setMenuOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isDrawerOpen, isMenuOpen]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((o) => !o), []);

  const isExpanded = useCallback(
    (l1Id: string) => expandedL1.has(l1Id),
    [expandedL1],
  );

  const toggleExpand = useCallback((l1Id: string) => {
    setExpandedL1((prev) => {
      const next = new Set(prev);
      if (next.has(l1Id)) next.delete(l1Id);
      else next.add(l1Id);
      return next;
    });
  }, []);

  return {
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    isMenuOpen,
    openMenu,
    closeMenu,
    toggleMenu,
    expandedL1,
    isExpanded,
    toggleExpand,
    activeL1,
    activeL2,
  };
}
